import {
  Heart,
  ListMusic,
  Loader2,
  Maximize2,
  Pause,
  Play,
  SkipBack,
  SkipForward,
  CheckCircle2,
  RotateCcw,
  RotateCw,
  Volume2,
  VolumeX,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { routeBuilders } from '../routes';
import { slugify } from '../../utils/slug';
import { usePlayerController } from './PlayerProvider';
import { usePlayerStore } from './playerStore';
import { shallow } from 'zustand/shallow';

type DockPlayerProps = {
  onToggleLike?: (seriesId: string) => void;
  isLiked?: boolean;
  onMarkComplete?: () => void;
};

const formatTime = (seconds?: number | null) => {
  if (!seconds || Number.isNaN(seconds)) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

export function DockPlayer({ onToggleLike, isLiked = false, onMarkComplete }: DockPlayerProps) {
  const { togglePlay, next, previous, seek, openQueue, openFullView } = usePlayerController();
  const queue = usePlayerStore((state) => state.queue);
  const currentEpisodeId = usePlayerStore((state) => state.currentEpisodeId);
  const currentEpisode = queue.find((ep) => ep.id === currentEpisodeId) ?? null;
  const currentTime = usePlayerStore((state) => state.currentTime);
  const duration = usePlayerStore((state) => state.duration);
  const isPlaying = usePlayerStore((state) => state.isPlaying);
  const isBuffering = usePlayerStore((state) => state.isBuffering);
  const volume = usePlayerStore((state) => state.volume);
  const setVolume = usePlayerStore((state) => state.setVolume);

  if (!currentEpisode) return null;

  const seriesPath = currentEpisode.seriesSlug
    ? routeBuilders.seriesPublic(currentEpisode.seriesSlug)
    : undefined;
  const creatorSlug = currentEpisode.creatorSlug ?? (currentEpisode.creatorName ? slugify(currentEpisode.creatorName) : null);
  const creatorPath = creatorSlug ? routeBuilders.creatorPublic(creatorSlug) : undefined;

  return (
    <div className="fixed inset-x-0 bottom-0 z-40 px-3 pb-3">
      <div className="mx-auto max-w-6xl rounded-2xl border border-base-300 bg-base-200/80 backdrop-blur-xl shadow-2xl">
        <div className="flex flex-col gap-3 px-4 py-3 md:flex-row md:items-center md:gap-4">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="h-14 w-14 rounded-lg overflow-hidden bg-base-300 shadow-inner">
              {currentEpisode.coverImage ? (
                <img src={currentEpisode.coverImage} alt={currentEpisode.seriesTitle} className="h-full w-full object-cover" />
              ) : (
                <div className="h-full w-full bg-base-300" />
              )}
            </div>
            <div className="min-w-0">
              <p className="text-xs text-base-content/60 truncate">
                {creatorPath ? (
                  <Link to={creatorPath} className="hover:underline">
                    {currentEpisode.creatorName ?? currentEpisode.seriesTitle}
                  </Link>
                ) : (
                  currentEpisode.creatorName ?? currentEpisode.seriesTitle
                )}
              </p>
              <p className="font-semibold leading-snug truncate">
                {seriesPath ? (
                  <Link to={seriesPath} className="hover:underline">
                    {currentEpisode.seriesTitle}
                  </Link>
                ) : (
                  currentEpisode.seriesTitle
                )}
              </p>
              <p className="text-sm text-base-content/70 truncate">{currentEpisode.title}</p>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 justify-center md:justify-start flex-wrap">
              <button className="btn btn-circle btn-ghost btn-sm" onClick={previous} aria-label="Previous episode">
                <SkipBack className="h-5 w-5" />
              </button>
              <button
                className="btn btn-circle btn-ghost btn-sm"
                onClick={() => seek(Math.max(0, currentTime - 15))}
                aria-label="Go back 15 seconds"
              >
                <RotateCcw className="h-5 w-5" />
              </button>
              <button
                className="btn btn-circle btn-primary btn-md"
                onClick={togglePlay}
                aria-label={isPlaying ? 'Pause' : 'Play'}
              >
                {isBuffering ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : isPlaying ? (
                  <Pause className="h-6 w-6" />
                ) : (
                  <Play className="h-6 w-6 ml-0.5" />
                )}
              </button>
              <button
                className="btn btn-circle btn-ghost btn-sm"
                onClick={() => {
                  const target = duration ? Math.min(duration, currentTime + 15) : currentTime + 15;
                  seek(target);
                }}
                aria-label="Skip forward 15 seconds"
              >
                <RotateCw className="h-5 w-5" />
              </button>
              <button className="btn btn-circle btn-ghost btn-sm" onClick={next} aria-label="Next episode">
                <SkipForward className="h-5 w-5" />
              </button>
              <button
                className="btn btn-circle btn-ghost btn-sm"
                onClick={() => onMarkComplete?.()}
                aria-label="Mark completed"
              >
                <CheckCircle2 className="h-5 w-5" />
              </button>
            </div>
            <div className="mt-2 space-y-1">
              <div className="relative h-2 w-full rounded-full bg-base-300 overflow-hidden">
                <div
                  className="absolute inset-y-0 left-0 bg-primary transition-[width]"
                  style={{ width: `${duration ? Math.min((currentTime / duration) * 100, 100) : 0}%` }}
                />
                <input
                  type="range"
                  min={0}
                  max={duration || 1}
                  step={0.5}
                  value={Math.min(currentTime, duration || currentTime)}
                  onChange={(e) => seek(Number(e.target.value))}
                  className="absolute inset-0 opacity-0 cursor-pointer"
                  aria-label="Seek"
                />
              </div>
              <div className="flex items-center justify-between text-xs text-base-content/60">
                <span>{formatTime(currentTime)}</span>
                <span>{formatTime(duration)}</span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 md:gap-3 flex-wrap justify-between md:justify-end">
            <div className="hidden md:flex items-center gap-2">
              <div className="flex items-center gap-2">
                <button
                  className="btn btn-square btn-ghost btn-sm"
                  onClick={() => setVolume(volume > 0 ? 0 : 0.9)}
                  aria-label={volume > 0 ? 'Mute' : 'Unmute'}
                >
                  {volume > 0 ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
                </button>
                <input
                  type="range"
                  min={0}
                  max={1}
                  step={0.01}
                  value={volume}
                  onChange={(e) => setVolume(Number(e.target.value))}
                  className="range range-xs w-28"
                  aria-label="Volume"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                className={`btn btn-square btn-ghost ${isLiked ? 'text-primary' : ''}`}
                onClick={() => currentEpisode.seriesId && onToggleLike?.(currentEpisode.seriesId)}
                aria-label={isLiked ? 'Unlike series' : 'Like series'}
              >
                <Heart className={`h-5 w-5 ${isLiked ? 'fill-current' : ''}`} />
              </button>
              <button
                className="btn btn-ghost btn-sm md:btn-outline md:btn-sm"
                onClick={() => openQueue(true)}
                aria-label="View episodes"
              >
                <ListMusic className="h-4 w-4 md:mr-2" />
                <span className="hidden md:inline">View episodes</span>
              </button>
              <button
                className="btn btn-ghost btn-sm md:btn-outline md:btn-sm"
                onClick={() => openFullView(true)}
                aria-label="Expand player"
              >
                <Maximize2 className="h-4 w-4 md:mr-2" />
                <span className="hidden md:inline">Full view</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
